import React, { useEffect, useRef, useState } from 'react';
import { Dropdown, OverlayTrigger, Popover, Spinner, Table, ToggleButton } from 'react-bootstrap';
import {
  INVOICE_GENERATION_FILER_PENDING,
  INVOICE_GENERATION_FILER_ALL,
  INVOICE_GENERATION_FILER_GENERATED,
  INVOICE_PAGINATION_DEFAULT_SIZE,
  InvoiceDto,
  InvoiceGenerationFilters,
} from '../../models/invoice.dto';
import InvoicesListItem from './invoices-list-item/invoices-list-item';
import ModalContainer from '../layout/modal-container';
import ExportModal from './export-modal';
import {
  FilterInvoiceBody, useExportCsvMutation,
  useExportInvoicesMutation,
  useFilterInvoicesQuery,
} from '../../store/invoices/invoices.api';
import { groupBy } from '../../utils/groupBy';
import './invoices-list.scss';
import { AccountingNumberDto } from '../../models/accounting-number.dto';
import { CostCenterDto } from '../../models/cost-center.dto';
import { useTranslation } from 'react-i18next';
import SearchBar from './search-bar/search-bar';
import { formatDate } from '../../utils/dateFormatUtil';
import { useGetUserDataQuery } from '../../store/user/user.api';
import { CaretDownFill, CaretUpFill, GearFill } from 'react-bootstrap-icons';
import Form from 'react-bootstrap/Form';
import { getColumnsConfig } from './columns/columns';
import { useGetSettingsQuery, usePatchSettingsMutation } from '../../store/settings/settings.api';
import moment from 'moment';
import ActionDropdown from './action-dropdown/action-dropdown';
import { isEmptyArray, notBlank, notEmpty, notEmptyArray } from '../../utils/base';
import { IS_ADMIN } from '../../utils/role';
import ExportWarningModal from './export-warning-modal';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import exportChevronDown from '../../assets/icons/export-chevron-down.svg';
import AddInvoiceModal from '../add-invoice-modal/add-invoice-modal';
import DropdownWithFilter from '../elements/dropdown-with-filter/dropdown-with-filter';
import AccountitzTutorial from '../tutorial/accountitz-tutorial';


interface PropsType {
  accountingNumbers: AccountingNumberDto[];
  costCenters: CostCenterDto[];
  accountingNumbersInUse: AccountingNumberDto[];
  costCentersInUse: CostCenterDto[];
  handleInvoiceNumberOrCenterUpdate: () => void;
}

const defaultFilterParams: FilterInvoiceBody = {
  name: '',
  fromDate: '2022-01-01',
  toDate: moment().add(1, 'day').format('YYYY-MM-DD'),
  fromAmount: '',
  toAmount: '',
  costCenter: '',
  accountingNumber: '',
  generationDateFilter: INVOICE_GENERATION_FILER_ALL,
  page: 0,
  size: INVOICE_PAGINATION_DEFAULT_SIZE
};

const useDidMountEffect = (func, deps) => {
  const didMount = useRef(false);

  useEffect(() => {
    if (didMount.current) func();
    else didMount.current = true;
  }, deps);
}

const InvoicesList = ({
  accountingNumbersInUse, costCentersInUse, costCenters
}: PropsType) => {
  const [selectedInvoices, setSelectedInvoices] = useState<InvoiceDto[]>([]);
  const [shownInvoices, setShownInvoices] = useState<InvoiceDto[]>([]);
  const [paymentDate, setPaymentDate] = useState<Date>(new Date());
  const [filterParams, setFilterParams] = useState<FilterInvoiceBody>(defaultFilterParams);
  const [generationDateFilter, setGenerationDateFilter] = useState<InvoiceGenerationFilters>(INVOICE_GENERATION_FILER_ALL);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showExportWarningModal, setShowExportWarningModal] = useState<number[]>([]);
  const [showEmptyBankAccountModal, setShowEmptyBankAccountModal] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const [translate] = useTranslation(['common', 'invoice', 'account']);
  const [showActionModal, setShowActionModal] = useState<boolean>(false);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);

  // pagination states
  const [page, setPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isLoadingRef = useRef(false);

  const [patchSettings] = usePatchSettingsMutation();
  const { data: settings } = useGetSettingsQuery();

  const [sortBy, setSortBy] = useState(settings?.sortBy?.column || 'invoiceId');
  const [ascending, setAscending] = useState<boolean>(settings?.sortBy ? settings.sortBy?.order === 'ASC' : true);
  const [exportInvoices] = useExportInvoicesMutation();
  const [exportCsv] = useExportCsvMutation();

  const { data: filterInvoices } = useFilterInvoicesQuery({
    name: filterParams.name,
    fromDate: filterParams.fromDate,
    toDate: filterParams.toDate,
    fromAmount: filterParams.fromAmount,
    toAmount: filterParams.toAmount,
    costCenter: filterParams.costCenter,
    accountingNumber: filterParams.accountingNumber,
    generationDateFilter: filterParams.generationDateFilter,
    sortBy: sortBy,
    ascending: ascending,
    page: page,
    size: INVOICE_PAGINATION_DEFAULT_SIZE
  });
  const { data: user } = useGetUserDataQuery();
  const filterInvoicesContent = filterInvoices?.content;

  const handleInvoiceSelection = (invoice: InvoiceDto, checked: boolean) => {
    setShowActionModal(true);
    checked
      ? setSelectedInvoices([...selectedInvoices, invoice])
      : setSelectedInvoices(
        selectedInvoices.filter((item) => item.invoiceId !== invoice.invoiceId),
      );
  };

  useEffect(() => {
    setSelectedInvoices(
      selectedInvoices.map(
        (selectedInvoice) =>
          filterInvoicesContent?.find(
            (invoice) => invoice?.invoiceId === selectedInvoice?.invoiceId,
          ) as InvoiceDto,
      ),
    );
  }, [filterInvoices]);

  const handleExportToPaymentClick = () => {
    const alreadyPaidInvoices = selectedInvoices.filter(invoice => notBlank(invoice.dtaDate)).map(i => i.invoiceId);

    selectedInvoices.filter((invoice) => !!invoice).some((invoice) => !invoice?.bankAccount)
      ? setShowEmptyBankAccountModal(true)
      : isEmptyArray(alreadyPaidInvoices)
        ? setShowExportModal(true)
        : setShowExportWarningModal(alreadyPaidInvoices);
  };

  const getExportFileFormat = () =>
    Object.entries(groupBy(selectedInvoices, (i) => i.bankAccount.name)).length > 1
      ? 'zip'
      : 'xml';

  const resetSelectedInvoices = () => setSelectedInvoices([]);

  const handleSearchChange = (
    name,
    fromDate,
    toDate,
    fromAmount,
    toAmount,
    costCenter,
    accountingNumber,
    generationDateFilter,
    page,
    size
  ) => {
    setFilterParams({
      name,
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate),
      fromAmount,
      toAmount,
      costCenter,
      accountingNumber,
      generationDateFilter,
      page,
      size
    });
    setShownInvoices([]);
    resetSelectedInvoices();
    setPage(0);
    setHasMore(true);
  };

  const currentPage = filterInvoices?.number;

  const onSelectAllClicked = () => {
    if (!allSelected) {
      setAllSelected(true);
      setSelectedInvoices(shownInvoices);
      setShowActionModal(true);
    } else {
      setAllSelected(false);
      resetSelectedInvoices();
      setShowActionModal(false);
    }
  };

  const toggleAll = <ToggleButton
    style={{ padding: 6 }}
    type='checkbox'
    variant='outline-primary'
    checked={allSelected}
    value='allSelected'
    onClick={onSelectAllClicked}
    id={'toggle-all-btn'}
  />;

  const handleExportToCsvClick = () => {
    exportCsv(filterParams);
  }

  const patchInvoiceInLists = (updated: InvoiceDto) => {
    setShownInvoices(prev =>
       prev.map(i => (i.invoiceId === updated.invoiceId ? updated : i))
    );
    setSelectedInvoices(prev =>
       prev.map(i => (i.invoiceId === updated.invoiceId ? updated : i))
    );
  };

  const getSortedBy = (field: string) => {
    if (field === sortBy) {
      if (ascending) {
        return <CaretDownFill color='white' />;
      } else {
        return <CaretUpFill color='white' />;
      }
    }
  }

  const handleSortBy = (field: string) => {
    if (sortBy == field) {
      setAscending(a => !a);
    } else {
      setAscending(true);
      setSortBy(field);
    }
  }

  useDidMountEffect(() => {
    const sort = {
      column: sortBy,
      order: ascending ? 'ASC' : 'DESC' as 'ASC' | 'DESC'
    };
    patchSettings({ sortBy: sort });
  }, [ascending, sortBy])


  const costCentersExist = notEmptyArray(costCenters);

  const [columns, setColumns] = useState(
     getColumnsConfig(settings?.hiddenColumns ?? [], costCentersExist, patchInvoiceInLists)
  );

  useDidMountEffect(() => {
    const hiddenColumns = columns
      .filter(c => !c.show)
      .map(c => c.sortBy)
    patchSettings({ hiddenColumns });
  }, [columns])

  useEffect(() => {
    setColumns(getColumnsConfig(settings?.hiddenColumns ?? [], costCentersExist, patchInvoiceInLists));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.hiddenColumns, costCentersExist]);

  /** invite scroll use effects **/
  useEffect(() => {
    if (!filterInvoicesContent) return;

    setShownInvoices(prev => {
      if (currentPage === 0) return filterInvoicesContent;

      const next = [...prev];
      const indexById = new Map(next.map((i, idx) => [i.invoiceId, idx]));

      for (const inv of filterInvoicesContent) {
        const idx = indexById.get(inv.invoiceId);
        if (idx === undefined) {
          indexById.set(inv.invoiceId, next.length);
          next.push(inv);        // new invoice -> append
        } else {
          next[idx] = inv;       // existing -> replace IN PLACE (no jump)
        }
      }
      return next;
    });

    setHasMore(
       filterInvoicesContent.length > 0
          ? (filterInvoices?.totalPages ?? 0) > (currentPage ?? 0) + 1
          : false
    );

    setIsLoading(false);
    isLoadingRef.current = false;
  }, [filterInvoices, filterInvoices?.totalPages, currentPage]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.innerHeight + document.documentElement.scrollTop;
      const bottomPosition = document.documentElement.offsetHeight - 1;

      if (scrollPosition >= bottomPosition && hasMore && !isLoadingRef.current) {
        setIsLoading(true);
        isLoadingRef.current = true;
        setPage(prevPage => prevPage + 1);
      }
    };

    if (isLoading) return;
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, isLoading]);
  /** invite scroll use effects **/

  const popover = (
    <Popover id="popover-basic">
      <Popover.Body>
        {columns.map(c => (
          <Form.Check type='switch' label={translate(c.label)} id={c.label} key={c.label} checked={c.show} onClick={() => {
            setColumns(columns => {
              const i = columns.findIndex(i => c.label === i.label);
              columns[i].show = !columns[i].show;
              return [...columns];
            });
          }
          } />
        ))}
      </Popover.Body>
    </Popover>
  );

  const dataForExcelExport = () => {
    return selectedInvoices.map(i => {
      const data = {
        name: i.invoiceId,
        company: i.company,
        status: notEmpty(i.approvals) ? 'Approved' : notEmpty(i.rejections) ? 'Rejected' : 'Pending',
        amount: i.amount,
        currency: i.currency,
        bankAccount: i.bankAccount?.name,
        vatCode: i.vat ? i.vat.code : '',
        accountingNumber: i.accountingNumber,
        accountingRemark: i.accountingRemark,
        description: i.description,
        generationDate: i.dtaDate ? moment(i.dtaDate).format('DD-MM-YYYY') : '',
        paymentDate: i.paymentDate ? moment(i.paymentDate).format('DD-MM-YYYY') : '',
        costCenter: i.costCenter
      }

      if (costCentersExist) {
        data.costCenter = i.costCenter;
      }

      return data
    });
  }

  const handleExportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(dataForExcelExport());
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoices');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'invoices.xlsx');
  };

  const countTotalInvoices = filterInvoices?.totalElements ?? 0;
  const statusFilterOptions: {value: InvoiceGenerationFilters; label: string}[] = [
    { value: INVOICE_GENERATION_FILER_ALL, label: translate('common:common.all') },
    { value: INVOICE_GENERATION_FILER_PENDING, label: translate('common:common.unpaid') },
    { value: INVOICE_GENERATION_FILER_GENERATED, label: translate('common:common.paid') },
  ];
  const selectedStatusLabel = statusFilterOptions.find(option => option.value === generationDateFilter)?.label;

  return (
    <>
      <div className="header">
        <div className='title-row'>
          <div className="title">
            {translate('invoice:invoice.invoices')}
            {countTotalInvoices > 0 &&
               <sup className="title-counter">{countTotalInvoices}</sup>
            }
          </div>
          <div className='mobile-status-filter' data-tour='mobile-invoice-tabs'>
            <DropdownWithFilter
              items={statusFilterOptions}
              selectedValue={selectedStatusLabel ?? ''}
              getMenuItemValue={option => option.label}
              handleChange={option => setGenerationDateFilter(option.value)}
              handleFilter={items => items}
              className='mobile-status-filter-toggle'
              searchable={false}
            />
          </div>
        </div>
        {IS_ADMIN() &&
          <div className='actions' data-tour='invoice-actions'>
            <Dropdown onToggle={setIsExportMenuOpen}>
              <Dropdown.Toggle className="outline-primary dropdown-toggle" id="dropdown-basic">
                {translate('invoice:invoice.export')}
                <span className={`export-chevron ${isExportMenuOpen ? 'is-open' : ''}`} aria-hidden='true'>
                  <img src={exportChevronDown} alt='' />
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item
                  disabled={selectedInvoices?.length === 0}
                  onClick={handleExportToPaymentClick}
                  className='item'
                >
                  {translate('invoice:invoice.exportForPayment')}
                </Dropdown.Item>
                <Dropdown.Item onClick={handleExportToCsvClick} className='item'>
                  {translate('invoice:invoice.exportForCsv')}
                </Dropdown.Item>
                <Dropdown.Item
                  disabled={selectedInvoices?.length === 0}
                  onClick={handleExportToExcel}
                  className='item'
                >
                  {translate('invoice:invoice.exportToExcel')}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <AddInvoiceModal />
          </div>
        }
      </div>

      <SearchBar
        onSearch={handleSearchChange}
        searchParams={defaultFilterParams}
        costCenters={costCentersInUse}
        accountingNumbers={accountingNumbersInUse}
        generationDateFilter={generationDateFilter}
        setGenerationDateFilter={setGenerationDateFilter}
      />

      <div className='invoice-table-shell'>
        <div
          aria-hidden='true'
          className={`invoice-details-tour-target${shownInvoices.length > 0 ? ' has-first-row' : ''}`}
          data-tour='invoice-details'
        />
        <Table className="custom-table invoice-table" table-borderless='true' hover responsive>
          <thead>
            <tr>
              <th className='selection'>{toggleAll}</th>
              {columns.map(c => {
                if (c.show) {
                  return (<th className={c.className} onClick={() => handleSortBy(c.sortBy)} key={c.label}>
                    {translate(c.label)} {getSortedBy(c.sortBy)}
                  </th>);
                }
              }
              )}
              <th className={'action'}>
                <OverlayTrigger trigger="click" placement="bottom" overlay={popover}>
                  <GearFill />
                </OverlayTrigger>
              </th>
            </tr>
          </thead>
          <tbody>
            {shownInvoices.map((invoice, index) => (
              <InvoicesListItem
                columns={columns}
                invoice={invoice}
                selected={selectedInvoices}
                handleInvoiceSelection={handleInvoiceSelection}
                key={invoice.invoiceId}
                user={user}
                costCentersExist={costCentersExist}
                isTutorialTarget={index === 0}
              />
            ))}
          </tbody>
        </Table>
      </div>

      {shownInvoices.length === 0 && (
        <div className='noinvoice' data-tour='invoice-details-mobile'>
          {translate('invoice:invoice.noInvoices')}
        </div>
      )}
      {isLoading && <div className='spinner-wrapper'> <Spinner animation="border" role="status" /> </div>}
      <ModalContainer
        modalContent={
          <ExportModal
            selectedInvoices={selectedInvoices}
            paymentDate={paymentDate}
            setPaymentDate={setPaymentDate}
          />
        }
        modalHeader={translate('invoice:invoice.exportPayment')}
        submitButtonText={translate('common:common.export')}
        show={showExportModal}
        onCancel={() => setShowExportModal(false)}
        onSubmit={() => {
          resetSelectedInvoices();
          exportInvoices({
            invoiceIds: selectedInvoices.map((invoice) => invoice.invoiceId),
            paymentDate: paymentDate.toISOString(),
            fileType: getExportFileFormat(),
          });
          setShowExportModal(false);
        }}
      />

      <ModalContainer
        modalContent={
          <ExportWarningModal
            invoiceIds={showExportWarningModal}
          />
        }
        modalHeader={translate('invoice:invoice.invoicesAlreadyPaid')}
        submitButtonText={translate('common:common.proceed')}
        show={notEmptyArray(showExportWarningModal)}
        onCancel={() => setShowExportWarningModal([])}
        onSubmit={() => {
          setShowExportWarningModal([])
          setShowExportModal(true);
        }}
      />

      <ModalContainer
        modalContent={
          <div>{translate('account:bankAccount.selectedBankAccountWarning')}</div>
        }
        modalHeader={translate('account:bankAccount.noBankAccountSelected')}
        submitButtonText={translate('common:common.close')}
        show={showEmptyBankAccountModal}
        onSubmit={() => setShowEmptyBankAccountModal(false)}
      />
      {showActionModal &&
         <ActionDropdown
           selectedInvoices={selectedInvoices}
           resetSelectedInvoices={resetSelectedInvoices}
           onClose={() => setShowActionModal(false)}
           setShownInvoices={setShownInvoices}
        />
      }

      <AccountitzTutorial
        completedVersion={user?.invoiceTutorialVersion}
        userKey={user?.id ? String(user.id) : user?.email}
      />

    </>
  );
};

export default InvoicesList;
